import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Star, Loader2, PenLine, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u.startsWith("/") ? `${API}${u}` : u);

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1" data-testid="review-star-picker">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => onChange(n)} aria-label={`${n} stars`}>
        <Star size={26} className={n <= value ? "fill-accent text-accent" : "text-border"} />
      </button>
    ))}
  </div>
);

const WriteReviewDialog = ({ open, onOpenChange }) => {
  const emptyForm = { name: "", location: "", rating: 5, text: "", type: "" };
  const [form, setForm] = useState(emptyForm);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type) || file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name} skipped (images only, max 8MB)`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API}/api/reviews/upload-image`, { method: "POST", body: fd });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail);
        setPhotos((prev) => [...prev, data.url].slice(0, 3));
      } catch {
        toast.error(`Could not upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Please add your name and your review");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, photos }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Could not submit your review");
        return;
      }
      toast.success("Thank you! Your review will appear once it's approved.");
      setForm(emptyForm);
      setPhotos([]);
      onOpenChange(false);
    } catch {
      toast.error("Could not submit your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="write-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Share Your Experience</DialogTitle>
          <DialogDescription className="font-body text-xs">
            Your review is published after a quick approval by our team.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="font-body text-sm">Your Rating</Label>
            <StarPicker value={form.rating} onChange={(rating) => setForm({ ...form, rating })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-body text-sm">Name *</Label>
              <Input data-testid="review-name" value={form.name} onChange={update("name")} placeholder="Jane D." className="font-body" />
            </div>
            <div>
              <Label className="font-body text-sm">Location</Label>
              <Input data-testid="review-location" value={form.location} onChange={update("location")} placeholder="Dallas, TX" className="font-body" />
            </div>
          </div>
          <div>
            <Label className="font-body text-sm">Stay Type</Label>
            <Input data-testid="review-type" value={form.type} onChange={update("type")} placeholder="Family Vacation, Romantic Getaway..." className="font-body" />
          </div>
          <div>
            <Label className="font-body text-sm">Your Review *</Label>
            <Textarea data-testid="review-text" value={form.text} onChange={update("text")} rows={4} placeholder="Tell future guests about your stay..." className="font-body" />
          </div>
          <div>
            <Label className="font-body text-sm">Photos (up to 3)</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {photos.map((p) => (
                <div key={p} className="relative">
                  <img src={resolveUrl(p)} alt="Review" className="h-16 w-20 object-cover rounded-md border border-border/60" />
                  <button
                    onClick={() => setPhotos(photos.filter((x) => x !== p))}
                    className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-border shadow p-0.5"
                    aria-label="Remove photo"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <Button data-testid="review-photo-upload" type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="font-body rounded-full">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={13} className="mr-1" /> Add Photo</>}
                </Button>
              )}
              <input ref={fileRef} data-testid="review-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={handleFiles} />
            </div>
          </div>
          <Button data-testid="review-submit" onClick={submit} disabled={submitting || uploading} className="w-full bg-accent hover:bg-accent/90 text-white font-body rounded-full h-11">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : "Submit Review"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ReviewCard = ({ review, delay, onPhotoClick }) => (
  <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay }} className="break-inside-avoid mb-6">
    <Card className="border border-border/50 p-6" data-testid={`review-card-${review.id}`}>
      <div className="flex items-center gap-1 mb-3">
        {[...Array(review.rating)].map((_, j) => (
          <Star key={j} size={14} className="fill-accent text-accent" />
        ))}
      </div>
      <p className="font-body text-sm text-foreground/80 leading-relaxed mb-4 italic">&ldquo;{review.text}&rdquo;</p>
      {review.photos?.length > 0 && (
        <div className="flex gap-2 mb-4">
          {review.photos.map((p) => (
            <img
              key={p}
              src={resolveUrl(p)}
              alt={`By ${review.name}`}
              className="h-20 w-24 object-cover rounded-md cursor-pointer hover:opacity-85 transition-opacity"
              onClick={() => onPhotoClick(resolveUrl(p))}
              loading="lazy"
            />
          ))}
        </div>
      )}
      <p className="font-heading text-base font-semibold text-foreground">{review.name}</p>
      <p className="font-body text-xs text-muted-foreground">
        {[review.location, review.type].filter(Boolean).join(" · ")}
      </p>
    </Card>
  </motion.div>
);

export default function ReviewsPage() {
  const [reviews, setReviews] = useState(null);
  const [writeOpen, setWriteOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/reviews`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setReviews)
      .catch(() => setReviews([]));
  }, []);

  return (
    <>
      <Helmet>
        <title>Guest Reviews | Texoma Resorts at Lake Texoma</title>
        <meta name="description" content="Read real guest reviews and photos from stays at Serenity Village — luxury cabins, glamping tents, and domes at Lake Texoma." />
      </Helmet>

      <section className="pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-accent mb-3">Guest Stories</p>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-foreground tracking-tight">
                Reviews
              </h1>
              <Button data-testid="write-review-button" onClick={() => setWriteOpen(true)} className="bg-accent hover:bg-accent/90 text-white font-body rounded-full">
                <PenLine size={15} className="mr-2" /> Write a Review
              </Button>
            </div>
            <p className="font-body text-base md:text-lg text-muted-foreground mt-4 max-w-xl">
              Real stories and photos from guests who found their serenity at Lake Texoma.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="reviews-wall" className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {reviews === null ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>
          ) : reviews.length === 0 ? (
            <p className="font-body text-muted-foreground">No reviews yet — be the first to share your stay!</p>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
              {reviews.map((r, i) => (
                <ReviewCard key={r.id} review={r} delay={(i % 6) * 0.08} onPhotoClick={setLightbox} />
              ))}
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="review-lightbox">
          <button className="absolute top-6 right-6 text-white/70 hover:text-white" onClick={() => setLightbox(null)} aria-label="Close">
            <X size={28} />
          </button>
          <img src={lightbox} alt="Guest" className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <WriteReviewDialog open={writeOpen} onOpenChange={setWriteOpen} />
    </>
  );
}
